import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import AddIdeaDialog from "@/components/AddIdeaDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { IDEA_CATEGORIES } from "@/lib/constants";
import { Plus, MoreVertical, Pencil, Trash2, Lightbulb } from "lucide-react";

export default function IdeaVault() {
  const [ideas, setIdeas] = useState([]);
  const [filter, setFilter] = useState("All");
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/ideas"); setIdeas(r.data); }
    catch (e) { toast.error("Could not load your ideas."); }
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("sfos:refresh", h);
    return () => window.removeEventListener("sfos:refresh", h);
  }, [load]);

  const remove = async () => {
    try { await api.delete(`/ideas/${deleting.id}`); toast.success("Idea deleted."); setDeleting(null); load(); }
    catch (e) { toast.error("Could not delete."); }
  };

  const visible = filter === "All" ? ideas : ideas.filter((i) => i.category === filter);

  return (
    <div>
      <PageHeader
        testid="ideas-header"
        title="Idea Vault"
        subtitle="A quiet place to keep every spark, until it's ready to become something."
        action={
          <Button onClick={() => { setEditing(null); setAddOpen(true); }} data-testid="add-idea-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
            <Plus className="h-4 w-4" /> Save idea
          </Button>
        }
      />

      <div className="mb-6">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="rounded-full w-52 bg-white border-border" data-testid="idea-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="All">All categories</SelectItem>
            {IDEA_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {visible.length === 0 ? (
        <Card className="rounded-3xl border-border shadow-soft p-12 text-center" data-testid="ideas-empty">
          <div className="h-12 w-12 rounded-full bg-accent/15 flex items-center justify-center mx-auto mb-3">
            <Lightbulb className="h-6 w-6 text-accent" />
          </div>
          <p className="text-muted-foreground">No ideas here yet. Capture the next one before it drifts away.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ideas-grid">
          {visible.map((i) => (
            <Card key={i.id} className="rounded-3xl border-border shadow-soft p-5 hover:shadow-soft-lg transition-shadow flex flex-col" data-testid={`idea-card-${i.id}`}>
              <div className="flex items-start justify-between gap-2">
                <Badge variant="outline" className="rounded-full border-border text-xs font-normal text-secondary">{i.category}</Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 rounded-full hover:bg-muted text-muted-foreground" data-testid={`idea-menu-${i.id}`}><MoreVertical className="h-4 w-4" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-2xl w-48">
                    <DropdownMenuItem onClick={() => { setEditing(i); setAddOpen(true); }} className="rounded-xl cursor-pointer"><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setDeleting(i)} className="rounded-xl cursor-pointer text-destructive focus:text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <h3 className="font-heading text-lg mt-3 leading-snug">{i.title}</h3>
              {i.notes && <p className="text-sm text-muted-foreground mt-2 line-clamp-4 flex-1">{i.notes}</p>}
            </Card>
          ))}
        </div>
      )}

      <AddIdeaDialog open={addOpen} onOpenChange={setAddOpen} idea={editing} onSaved={load} />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this idea?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.title}" will be removed from your vault.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-idea" className="rounded-full bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
